import type { Data } from '@strapi/types';
import { useMutation } from '@tanstack/react-query';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useApi } from '@/modules/utils/custom-hooks/use-api';

import { deleteProjectAttachmentApi, downloadProjectAttachmentApi, uploadProjectAttachmentApi } from '../api/project-file.api';

export const useProjectFileApi = () => {
	const { onSuccess } = useApi({ queryKey: ['GET_PROJECT_FILES'] });
	const { showToast } = useAppContext();

	const uploadProjectAttachment = useMutation({
		mutationKey: ['[POST]:UPLOAD_PROJECT_ATTACHMENT'],
		mutationFn: ({
			projectId,
			body,
		}: {
			projectId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
			body: FormData;
		}) => uploadProjectAttachmentApi({ projectId, body }),
		onSuccess: () => onSuccess('T_SUCCESS_ATTACHMENT_UPLOADED'),
		onError: () => showToast({ message: 'T_ERROR_UPLOADING_FILE', variant: 'error' }),
	});

	const downloadProjectAttachment = useMutation({
		mutationKey: ['[GET]:DOWNLOAD_PROJECT_ATTACHMENT'],
		mutationFn: ({ documentId }: { documentId: Pick<Data.Entity<'api::project-file.project-file'>, 'documentId'>['documentId'] }) =>
			downloadProjectAttachmentApi(documentId),
	});

	const deleteProjectAttachment = useMutation({
		mutationKey: ['[DELETE]:DELETE_PROJECT_ATTACHMENT'],
		mutationFn: ({ documentId }: { documentId: Pick<Data.Entity<'api::project-file.project-file'>, 'documentId'>['documentId'] }) =>
			deleteProjectAttachmentApi({ documentId }),
		onSuccess: () => onSuccess('T_SUCCESS_ATTACHMENT_DELETED'),
	});

	const isPending = uploadProjectAttachment.isPending || deleteProjectAttachment.isPending;

	return {
		downloadProjectAttachment,
		uploadProjectAttachment,
		deleteProjectAttachment,
		isPending,
	};
};
